import React, { createContext, useContext, useEffect, useState } from 'react';
import {
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
    onAuthStateChanged,
    signInWithPopup,
    updateProfile
} from 'firebase/auth';
import { auth, googleProvider, db } from '../firebase';
import { doc, setDoc, getDoc } from 'firebase/firestore';

const AuthContext = createContext({});

export const useAuth = () => {
    return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Sign up with email and password
    const signup = async (email, password, name, phone, userType) => {
        try {
            const result = await createUserWithEmailAndPassword(auth, email, password);

            // Update user profile with name
            await updateProfile(result.user, {
                displayName: name
            });

            // Store additional user data in Firestore
            await setDoc(doc(db, 'users', result.user.uid), {
                name,
                email,
                phone,
                userType,
                createdAt: new Date().toISOString()
            });

            return result;
        } catch (error) {
            throw error;
        }
    };

    // Sign in with email and password
    const login = (email, password) => {
        return signInWithEmailAndPassword(auth, email, password);
    };

    // Sign in with Google
    const loginWithGoogle = async () => {
        try {
            const result = await signInWithPopup(auth, googleProvider);
            let isNewUser = false;

            // Check if user document exists, if not create it
            const userDoc = await getDoc(doc(db, 'users', result.user.uid));
            if (!userDoc.exists()) {
                isNewUser = true;
                await setDoc(doc(db, 'users', result.user.uid), {
                    name: result.user.displayName,
                    email: result.user.email,
                    phone: '',
                    userType: 'client', // Default to client for Google sign-in
                    createdAt: new Date().toISOString()
                });
            }

            return {
                user: result.user,
                isNewUser
            };
        } catch (error) {
            throw error;
        }
    };

    // Logout
    const logout = () => {
        return signOut(auth);
    };

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            if (user) {
                // If no users doc exists, check if they are a service provider
                // logging into the customer app and auto-create a profile so
                // pages like Personal Details and Settings work correctly.
                const userDocRef = doc(db, 'users', user.uid);
                const userSnap = await getDoc(userDocRef);
                if (!userSnap.exists()) {
                    const providerSnap = await getDoc(doc(db, 'serviceProviders', user.uid));
                    if (providerSnap.exists()) {
                        const pd = providerSnap.data();
                        await setDoc(userDocRef, {
                            name: pd.name || user.displayName || '',
                            email: pd.email || user.email || '',
                            phone: pd.phone || '',
                            userType: 'client',
                            isServiceProvider: true,
                            createdAt: new Date().toISOString(),
                        });
                    }
                }
            }
            setCurrentUser(user);
            setLoading(false);
        });

        return unsubscribe;
    }, []);

    const value = {
        currentUser,
        signup,
        login,
        loginWithGoogle,
        logout,
        loading
    };

    return (
        <AuthContext.Provider value={value}>
            {!loading && children}
        </AuthContext.Provider>
    );
};
